import * as _ from "lodash-es";
import IConnection_Token from "../models/IConnection_Token";
import INewAddress from "../models/INewAddress";
import ISupplierModel from "../models/ISupplierModel";
import ISupplierSettings from "../models/ISupplierSettings";
import UserTypesEnum from "../models/UserTypesEnum";

const getLocalSupplierData = (): any => {
    return new Promise((resolve, reject) => {
        try {
            const Supplier: any = localStorage.getItem("Supplier");

            if (_.isNil(Supplier)) {
                reject(new Error("Error finding Supplier in local storage."));
            } else {
                const parsedSupplier = JSON.parse(Supplier);

                resolve(parsedSupplier);
            }
        } catch (err) {
            reject(err);
        }
    });
};

const getLocalSupplierDataFn = (): ISupplierModel => {
    const Supplier: any = localStorage.getItem("Supplier");

    if (!_.isNil(Supplier)) {
        const parsedSupplier = JSON.parse(Supplier);

        return parsedSupplier;
    } else {
        return {} as ISupplierModel;
    }
};

const saveSupplierData = (data: any = {}) => {
    if (_.isNil(data)) {
        data = {};
    }

    data.dataAge = Date.now();

    // we need to strip out some of the QBD elements that cause this to be too much data stored in local storage.

    const _SupplierData = JSON.stringify(data);

    localStorage.setItem("Supplier", _SupplierData);
};

const _clearSupplierData = () => {
    return new Promise((resolve, reject) => {
        try {
            localStorage.removeItem("Supplier");

            resolve(true);
        } catch (err) {
            reject(err);
        }
    });
};

const getStep = (supplierModel: ISupplierModel) => {
    let step = 1;

    switch (supplierModel.qty_precision) {
        case 1:
            step = 0.1;
            break;

        case 2:
            step = 0.01;
            break;

        case 3:
            step = 0.001;
            break;

        default:
            step = 1;
            break;
    }

    return step;
};

const updateStringWithVendorOverride = (str: string, supplierModel: ISupplierModel): string => {
    let updatedString = str;

    if (!_.isNil(supplierModel.vendor_name_override)) {
        updatedString = updatedString.replace(/vendor/g, supplierModel.vendor_name_override);
    }

    return updatedString;
};

const updateStringWithOrderNameOverride = (str: string, supplierModel: ISupplierModel, options?: { uppercaseFirst?: boolean }): string => {
    let updatedString = str;

    if (!_.isNil(supplierModel.order_name_override)) {
        updatedString = updatedString.replace(/order/gi, supplierModel.order_name_override);
    }

    if (options?.uppercaseFirst) {
        updatedString = _.startCase(updatedString);
    }

    return updatedString;
};

const getValueWithQtyPrecision = (qty: number, supplierModel: ISupplierModel): number => {
    const value = Intl.NumberFormat("en-US", {
        minimumFractionDigits: supplierModel.qty_precision,
        maximumFractionDigits: supplierModel.qty_precision,
    }).format(qty);

    return Number(value);
};

const getSupplierSettings = (supplierModel: ISupplierModel): ISupplierSettings => {
    return typeof supplierModel.settings === "string" ? JSON.parse(supplierModel.settings) : supplierModel.settings;
};

const getSupplierSetting = (
    supplierSettings: ISupplierSettings,
    viewOption: string,
    defaultValue: boolean,
    settingsKey: "simple_packing_list" | "simple_pick_list" | "quote" | "invoice" = "simple_packing_list"
): boolean => {
    // @ts-expect-error
    if (supplierSettings[settingsKey] && !_.isNil(supplierSettings[settingsKey][viewOption])) {
        // @ts-expect-error
        return supplierSettings[settingsKey][viewOption];
    } else {
        return defaultValue;
    }
};

/**
 * OR-1500 feature gate: the route pick list export, the customer stop-order field and the
 * all-items toggle show only for suppliers with routes AND settings.route_pick_list.enabled.
 */
const hasRoutePickList = (supplierModel: ISupplierModel | null | undefined): boolean => {
    if (!supplierModel?.has_routes) {
        return false;
    }

    try {
        return getSupplierSettings(supplierModel)?.route_pick_list?.enabled === true;
    } catch (err) {
        return false;
    }
};

/**
 * Business Settings > Routes "customer stop order" toggle. True only when the route pick list is enabled for
 * the supplier (the toggle is offered only under that flag, so it cannot stay silently active once the flag is
 * withdrawn) AND settings.route_stop_order_enabled is true. Gates the stop-order field on the customer sidebar;
 * the API gates the customer CSV column the same way.
 */
const hasRouteStopOrder = (supplierModel: ISupplierModel | null | undefined): boolean => {
    if (!supplierModel || !hasRoutePickList(supplierModel)) {
        return false;
    }

    try {
        return getSupplierSettings(supplierModel)?.route_stop_order_enabled === true;
    } catch (err) {
        return false;
    }
};

const getFiscalYearStartMonth = (supplierModel: ISupplierModel): number => {
    let startMonth = 1;

    try {
        const settings = getSupplierSettings(supplierModel);
        const settingValue = Number(settings?.fiscal_year_start_month);

        if (Number.isInteger(settingValue) && settingValue >= 1 && settingValue <= 12) {
            startMonth = settingValue;
        }
    } catch {
        startMonth = 1;
    }

    return startMonth;
};

const getFiscalYearStartLabel = (startMonth: number, locale: string = "en"): string => {
    if (!Number.isInteger(startMonth) || startMonth < 1 || startMonth > 12) {
        return "";
    }

    return new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2024, startMonth - 1, 1)));
};

const isSupplierUserType = (supplierUserToken: IConnection_Token | undefined, userTypes: UserTypesEnum | UserTypesEnum[]): boolean => {
    if (!supplierUserToken) {
        return false;
    }

    const typesArray = Array.isArray(userTypes) ? userTypes : [userTypes];
    return typesArray.includes(supplierUserToken.User_Type?.id as UserTypesEnum);
};

const isAddressValidForCheckout = (supplierModel: ISupplierModel, address: INewAddress | null | undefined): boolean => {
    const hasTaxJar = supplierModel.Supplier_Integration?.has_taxjar === true;
    const hasSalesTax = supplierModel.has_sales_tax === true;
    const hasShippingByWeightState = supplierModel.has_shipping_by_weight_state === true;

    // If no conditions require address validation, return true
    if (!hasTaxJar && !hasSalesTax && !hasShippingByWeightState) {
        return true;
    }

    if (!address) return false;

    const hasState = Boolean(address.state?.trim());
    const hasPostalCode = Boolean(address.postal_code?.trim());
    const hasCity = Boolean(address.city?.trim());
    const hasCountry = Boolean(address.country?.trim());

    // TaxJar requires: state, postal_code, country
    if (hasTaxJar) {
        if (!hasState || !hasPostalCode || !hasCountry) return false;
    }

    // Sales tax and state-based shipping require: city, state, postal_code
    if (hasSalesTax || hasShippingByWeightState) {
        if (!hasCity || !hasState || !hasPostalCode) return false;
    }

    return true;
};

const SupplierService = {
    getLocalSupplierData,
    saveSupplierData,
    _clearSupplierData,
    getLocalSupplierDataFn,
    getStep,
    updateStringWithVendorOverride,
    updateStringWithOrderNameOverride,
    getValueWithQtyPrecision,
    getSupplierSettings,
    getSupplierSetting,
    getFiscalYearStartMonth,
    getFiscalYearStartLabel,
    hasRoutePickList,
    hasRouteStopOrder,
    isSupplierUserType,
    isAddressValidForCheckout,
};

export default SupplierService;
